/*
 * map.ts — typed map data model and frame-loop owner
 */

import Atmosphere from './atmosphere';
import Renderer from '../renderer/renderer';
import Inspector from '../inspector/inspector';
import MapCamera from './camera';
import * as positionCodecs from './position-codecs';
import EventBus from './event-bus';
import type ConfigStore from '../config-store';
import * as viewerConfig from '../viewer-config';
import type { vec3 } from '../utils/math';
import * as utils from '../utils/utils';
import { utilsUrl } from '../utils/url';
import { platform } from '../utils/platform';
import { grayPngDecodeAvailable } from '../utils/gray-png';
import LegacyMap from './legacy-map';
import { defaultOverrides, type Overrides } from './overrides';
import FrameProfiler from './frame-profiler';
import MapStyle from './style';
import VerticalExaggeration from './vertical-exaggeration';
import type * as StyleSchema from './style-schema';
import MapDraw from './draw';
import MapConvert from './convert';
import MapMeasure from './measure';
import MapSurfaceTree from './surface-tree';
import DrawTraversalMaskPool from './draw-traversal-mask';
import { drawTerrainTraversal } from './draw-traversal';
import type {
    TerrainTraversalAccounting,
    TerrainTraversalPass,
} from './draw-traversal';
import ColorTerrainSink from './color-terrain-sink';
import DepthTerrainSink from './depth-terrain-sink';
import ViewDescent from './view-descent';
import ProbeDescent from './probe-descent';
import ElevationStore from './elevation-store';
import type MapGeodataBuilder from './geodata-builder';
import type RasterSource from './raster-source';
import type TerrainSource from './terrain-source';


/** Pixel count of the canvas the cache baselines are configured for. */
const FullHdPixels = 1920 * 1080;


/**
 * The map data model — cartolina's central object. The typed
 * representation of a loaded map together with the logic that
 * operates on it. Holds the reference frame, surfaces, free layers,
 * atmosphere, current position, and registered overlays; exposes
 * the per-frame tick, the canvas-target draw,
 * coordinate conversion, and hit-testing. It also owns the engine
 * lifecycle: the animation-frame loop, map loading and unloading,
 * the event bus, request transformation, and the constructed
 * `Renderer` and `Inspector` (absorbed from the retired `core.js`
 * shell).
 *
 * UI-facing concerns live on `Viewer` (`src/viewer/viewer.ts`);
 * the GPU and shader work lives on `Renderer`
 * (`src/renderer/renderer.ts`). Legacy modules still reach
 * this instance through their `core` back-references.
 *
 * Map-model state and behaviour that still lives in JavaScript
 * sit on `LegacyMap` (`src/map/legacy-map.js`); they absorb into
 * `Map` as feature work touches them.
 *
 * Public types are re-exported under `Map.*` via declaration
 * merging at the bottom of this file. Consumers should write
 * `Map.OverlaySpec`, `Map.ViewerEventMap`, etc.
 */
class Map {

    // -----------------------------------------------------------------
    // Lifecycle
    // -----------------------------------------------------------------

    /**
     * @param element canvas element to render into
     * @param configStore runtime config store, already seeded with
     *   the caller's normalized options
     * @param style style URL or inline style to load
     * @param position optional initial camera position
     * @param transformRequest optional resource-request hook
     */
    constructor(
        element: HTMLElement,
        configStore: ConfigStore<viewerConfig.ViewerConfig>,
        style: string | StyleSchema.StyleSpecification,
        position?: Map.PositionInput,
        transformRequest?: utils.TransformRequestCallback,
    ) {

        this.configStore_ = configStore;
        this.config = configStore.values;
        this.transformRequest = transformRequest;
        this.element = element;

        this.readyPromise_ = new Promise((resolve, reject) => {
            this.resolveReady_ = resolve;
            this.rejectReady_ = reject;
        });

        this.inspector = Inspector != null ? new Inspector(this) : null;
        this.renderer = new Renderer(this, element, this.config);

        // after the renderer: the scale denominator reads the canvas
        // size through it
        this.verticalExaggeration =
            new VerticalExaggeration(this, this.config);

        platform.init();

        void this.loadMapFromStyle(style, position).catch(
            (error: unknown) => this.reportLoadFailure(error));

        window.requestAnimationFrame(this.onUpdate_.bind(this));
    }

    /**
     * Destroys the map and releases all owned resources. `onRemove`
     * fires for every added overlay before teardown so overlays can
     * still draw through the live renderer.
     */
    [Symbol.dispose](): void {

        if (this.disposed_) return;
        this.disposeOverlays();
        this.disposeElevationStore();
        this.disposeTerrainMaskPool();
        this.destroyLoadedMap();
        this.rasterSources_.clear();
        this.terrainSources_.clear();
        this.renderer[Symbol.dispose]();
        this.element = null;
        this.killed = true;
        this.disposed_ = true;
    }

    /** Resolves once the map is fully loaded and ready to render. */
    get ready(): Promise<void> {

        this.assertAlive();
        return this.readyPromise_;
    }

    /** Whether the current map has fired its `map-loaded` completion. */
    get loaded(): boolean {

        return this.mapLoadedFired_;
    }

    // -----------------------------------------------------------------
    // Legacy accessors
    // -----------------------------------------------------------------

    /**
     * Returns the loaded legacy map, or `null`.
     *
     * @internal Legacy calling convention used by the JS modules.
     */
    getMap(): LegacyMap | null {

        return this.map;
    }

    /**
     * Returns the renderer.
     *
     * @internal Legacy calling convention used by the JS modules.
     */
    getRenderer(): Renderer {

        return this.renderer;
    }

    /**
     * Marks the loaded map dirty, forcing a redraw.
     *
     * @internal Legacy calling convention used by the JS modules.
     */
    markDirty(): void {

        this.map?.markDirty();
    }

    /**
     * The event bus. Legacy emitters and subscribers reach it as
     * `core.bus`.
     *
     * @internal
     */
    get bus(): EventBus<Map.ViewerEventMap> {

        return this.bus_;
    }

    /**
     * The runtime config store.
     *
     * @internal
     */
    get configStore(): ConfigStore<viewerConfig.ViewerConfig> {

        return this.configStore_;
    }

    // -----------------------------------------------------------------
    // Events
    // -----------------------------------------------------------------

    /**
     * Subscribes to a named map event.
     *
     * @param eventName event to subscribe to
     * @param callback invoked each time the event fires
     * @returns unsubscribe function
     */
    on<K extends keyof Map.ViewerEventMap & string>(
        eventName: K,
        callback: (event: Map.ViewerEventMap[K]) => void,
    ): (() => void) {

        this.assertAlive();
        return this.bus_.on(eventName, callback);
    }

    /**
     * Subscribes to a named map event for a single invocation.
     *
     * @param eventName event to subscribe to
     * @param callback invoked once when the event fires
     * @returns unsubscribe function
     */
    once<K extends keyof Map.ViewerEventMap & string>(
        eventName: K,
        callback: (event: Map.ViewerEventMap[K]) => void,
    ): (() => void) {

        this.assertAlive();
        return this.bus_.once(eventName, callback);
    }

    /**
     * Fires a named map event to all subscribed listeners.
     *
     * @internal Emission is reserved for the library; applications
     *   only subscribe.
     * @param eventName event to fire
     * @param event payload passed to each listener
     */
    emit<K extends keyof Map.ViewerEventMap & string>(
        eventName: K,
        event: Map.ViewerEventMap[K],
    ): void {

        this.bus_.emit(eventName, event);
    }

    // -----------------------------------------------------------------
    // Rendering controls
    // -----------------------------------------------------------------

    /**
     * Sets the renderer feature flags (labels, atmosphere, shading).
     *
     * @param options rendering feature flags
     */
    setRenderingOptions(options: Renderer.RenderingOptions): void {

        this.assertAlive();
        this.renderer.setRenderingOptions(options);
    }

    /** Returns the current renderer feature flags. */
    getRenderingOptions(): Renderer.RenderingOptions | null {

        this.assertAlive();
        return this.renderer.getRenderingOptions();
    }

    /**
     * Sets the renderer illumination definition.
     *
     * @param spec illumination definition
     */
    setIllumination(spec: Renderer.IlluminationDef): void {

        this.assertAlive();
        this.renderer.setIllumination(spec);
    }

    /** Returns the current renderer illumination definition. */
    getIllumination(): Renderer.IlluminationDef | null {

        this.assertAlive();
        return this.renderer.getIllumination();
    }

    /**
     * Sets the vertical exaggeration ramps.
     *
     * @param spec vertical exaggeration ramp specification
     */
    setVerticalExaggeration(
        spec: VerticalExaggeration.Spec,
    ): void {

        this.assertAlive();
        this.verticalExaggeration.set(spec);
        this.markDirty();
    }

    /** Returns the current vertical exaggeration ramps. */
    getVerticalExaggeration(): VerticalExaggeration.Spec | null {

        this.assertAlive();
        return this.verticalExaggeration.get();
    }

    /**
     * Returns the map scale denominator for the given view extent.
     *
     * @param extent view extent in map units (metres)
     */
    getScaleDenominator(extent: number): number {

        this.assertAlive();
        return this.verticalExaggeration.scaleDenominator(extent);
    }

    /**
     * Returns the vertical exaggeration scale-ramp factor at the given
     * view extent.
     */
    getVeScaleFactor(viewExtent: number): number {

        this.assertAlive();
        return this.verticalExaggeration.scaleFactor(viewExtent);
    }

    /**
     * Semi-major axis of the body's reference ellipsoid, in metres,
     * read from the physical SRS.
     *
     * @internal Reached as `renderer.map.bodyMajorAxis` by the legacy
     *   JavaScript draw code.
     */
    get bodyMajorAxis(): number {

        return this.map!.getPhysicalSrs().getSrsInfo().a;
    }

    /**
     * Ratio of the body's semi-major to its semi-minor axis. Scaling a
     * physical z coordinate by it maps the ellipsoid onto a sphere of
     * the semi-major axis.
     *
     * @internal Reached as `renderer.map.bodyMajorToMinor` by the
     *   legacy JavaScript draw code.
     */
    get bodyMajorToMinor(): number {

        return this.map!.getPhysicalSrs().getSrsInfo().majorToMinor;
    }

    /**
     * Sets live atmosphere parameters on the loaded map.
     *
     * Fields omitted from `spec` are cleared. When the loaded map
     * cannot render an atmosphere, warns and leaves state unchanged.
     *
     * @param spec atmosphere runtime parameters
     */
    setAtmosphere(spec: Atmosphere.RuntimeParameters): void {

        this.assertAlive();
        if (!this.map) return;

        if (!this.map.atmosphere) {

            utils.warnOnce(
                'setAtmosphere ignored: this map cannot render an '
                + 'atmosphere (the body declares no atmosphere '
                + 'parameters or the density service is missing).');
            return;
        }

        this.map.atmosphere.setRuntimeParameters(spec);
    }

    /** Returns live atmosphere parameters from the loaded map. */
    getAtmosphere(): Atmosphere.RuntimeParameters | null {

        this.assertAlive();
        return this.map?.atmosphere?.getRuntimeParameters() ?? null;
    }

    // -----------------------------------------------------------------
    // Runtime visibility
    // -----------------------------------------------------------------

    /**
     * Installs the complete result of terrain metadata resolution.
     *
     * @internal Called once by `MapStyle.loadStyle`.
     */
    setTerrainSourceEntries(
        sources: globalThis.Map<string, TerrainSource>,
    ): void {

        this.terrainSources_ = sources;
    }

    /**
     * Registers one terrain source added at runtime.
     *
     * @param id source identifier
     * @param source the resolved terrain source
     * @internal Called by `MapStyle.addSource`.
     */
    addTerrainSourceEntry(id: string, source: TerrainSource): void {

        this.terrainSources_.set(id, source);
    }

    /**
     * Drops one terrain source registered at runtime.
     *
     * @param id source identifier
     * @internal Called by `MapStyle.removeSource`.
     */
    removeTerrainSourceEntry(id: string): void {

        this.terrainSources_.delete(id);
    }

    /**
     * Returns the terrain source a style source id resolves to.
     *
     * @internal Style loading registers every terrain source before the
     * style becomes effective, so an exception here is an invariant
     * violation rather than a recoverable load event.
     */
    resolveTerrainSource(sourceId: string): TerrainSource {

        const source = this.terrainSources_.get(sourceId);

        if (!source) {
            throw new Error(`terrain.sources references "${sourceId}" but `
                + `no surface was loaded for that source`);
        }

        return source;
    }

    /**
     * Installs the complete result of raster metadata resolution.
     *
     * @internal Called once by `MapStyle.loadStyle`.
     */
    setRasterSourceEntries(
        entries: globalThis.Map<string, Map.RasterSourceEntry>,
    ): void {

        this.rasterSources_ = entries;
    }

    /**
     * Registers one raster source added at runtime.
     *
     * @param id source identifier
     * @param entry the resolved-source entry
     * @internal Called by `MapStyle.addSource`.
     */
    addRasterSourceEntry(id: string, entry: Map.RasterSourceEntry): void {

        this.rasterSources_.set(id, entry);
    }

    /**
     * Drops one raster source registered at runtime.
     *
     * @param id source identifier
     * @internal Called by `MapStyle.removeSource`.
     */
    removeRasterSourceEntry(id: string): void {

        this.rasterSources_.delete(id);
    }

    /**
     * Returns a usable raster source for the render path.
     *
     * @internal Effective-style validation guarantees that rendering never
     * reaches a failed source. An exception here is therefore an invariant
     * violation rather than a recoverable load event.
     */
    getRasterSource(sourceId: string): RasterSource {

        const entry = this.rasterSources_.get(sourceId);

        if (!entry) {
            throw new Error(`Raster source "${sourceId}" is not registered.`);
        }

        if (entry.status === 'failed') {
            throw new Error(`Active raster source "${sourceId}" is in the `
                + `failed state: ${entry.error.message}`);
        }

        return entry.source;
    }

    /**
     * Verifies that every current texture layer has a usable source.
     *
     * @internal Used for initial activation and prospective visibility state.
     */
    assertRasterSourcesAvailable(
        spec: StyleSchema.StyleSpecification,
    ): void {

        const activeTerrain = new Set(spec.terrain.sources);

        for (const layer of spec.layers ?? []) {

            const type = layer.type ?? 'diffuse-map';
            if (!['diffuse-map', 'bump-map', 'specular-map'].includes(type)) {
                continue;
            }

            // Resolve omitted applicability against every active terrain.
            const active = layer.terrain === undefined
                ? activeTerrain.size > 0
                : layer.terrain.some((id) => activeTerrain.has(id));
            if (!active) continue;

            const sourceId = layer.source as string;
            const entry = this.rasterSources_.get(sourceId);

            if (!entry) {
                throw new Error(`Raster layer "${layer.id}" references `
                    + `unregistered source "${sourceId}".`);
            }

            if (entry.status === 'failed') {
                throw new Error(`Raster layer "${layer.id}" cannot be `
                    + `activated because source "${sourceId}" failed to `
                    + `load: ${entry.error.message}`);
            }
        }
    }

    /**
     * Whether the `ready` promise has resolved. The runtime visibility
     * methods throw while this is `false`.
     *
     * @internal
     */
    get isReady(): boolean {

        return this.readyResolved_;
    }

    /**
     * Applies a complete visibility profile atomically.
     *
     * @param profile active terrain sources and every layer's terrain list
     * @throws before readiness or when the profile is invalid
     */
    applyVisibilityProfile(profile: Map.VisibilityProfile): void {

        this.assertAlive();
        this.requireReadyStyle().applyVisibilityProfile(profile);
    }

    /** Returns a complete copy of the current visibility state. */
    getVisibilityProfile(): Map.VisibilityProfile {

        this.assertAlive();
        return this.requireReadyStyle().getVisibilityProfile();
    }

    /**
     * Replaces the active terrain stack, preserving source order.
     *
     * @param sourceIds terrain source ids in stack order
     * @throws before readiness, on an invalid source id, or when the change
     *   would activate an unavailable raster source
     */
    setTerrainSources(sourceIds: string[]): void {

        this.assertAlive();
        this.requireReadyStyle().setTerrainSources(sourceIds);
    }

    /** Returns a copy of the current active terrain stack. */
    getTerrainSources(): string[] {

        this.assertAlive();
        return this.requireReadyStyle().getTerrainSources();
    }

    /**
     * Replaces one layer's active terrain-source list. An empty array
     * makes the layer inactive on every terrain.
     *
     * @param layerId id of the layer to change
     * @param terrainIds terrain source ids
     * @throws before readiness, on an invalid layer or source id, or when the
     *   change would activate an unavailable raster source
     */
    setLayerTerrainSources(layerId: string, terrainIds: string[]): void {

        this.assertAlive();
        this.requireReadyStyle().setLayerTerrainSources(layerId, terrainIds);
    }

    /**
     * Returns a copy of one layer's current terrain-source list.
     *
     * @param layerId id of the layer to query
     */
    getLayerTerrainSources(layerId: string): string[] {

        this.assertAlive();
        return this.requireReadyStyle().getLayerTerrainSources(layerId);
    }

    // -----------------------------------------------------------------
    // Coordinate conversion and hit-testing
    // -----------------------------------------------------------------

    /**
     * Converts public (lon/lat/height) coordinates to navigation
     * (Cartesian) coordinates. Heights are measured from the datum.
     *
     * @param pos `[lon, lat, height]` in public space
     */
    convertCoordsFromPublicToNav(pos: vec3): vec3 | null {

        this.assertAlive();
        return this.map?.convertCoordsFromPublicToNav(pos) ?? null;
    }

    /**
     * Projects navigation (Cartesian) coordinates onto the canvas.
     *
     * Returns `[x, y, depth]` in CSS pixels. A point is visible when
     * `depth <= 1` (in front of the camera).
     *
     * @param pos `[x, y, z]` in navigation space, the height measured
     *   from the datum
     */
    convertCoordsFromNavToCanvas(pos: vec3): vec3 | null {

        this.assertAlive();
        return this.map?.convertCoordsFromNavToCanvas(pos) ?? null;
    }

    /**
     * Converts navigation coordinates to public (lon/lat/height)
     * coordinates. Heights are measured from the datum.
     *
     * @param pos `[x, y, z]` in navigation space
     */
    convertCoordsFromNavToPublic(pos: vec3): vec3 | null {

        this.assertAlive();
        return this.map?.convertCoordsFromNavToPublic(pos) ?? null;
    }

    /**
     * Converts navigation coordinates to physical (ECEF) coordinates.
     *
     * @param pos `[x, y, z]` in navigation space, the height measured
     *   from the datum
     * @param includeSE whether to apply super-elevation
     */
    convertCoordsFromNavToPhys(pos: vec3, includeSE?: boolean): vec3 | null {

        this.assertAlive();
        return this.map?.convertCoordsFromNavToPhys(pos, includeSE) ?? null;
    }

    /**
     * Converts physical (ECEF) coordinates to camera space.
     *
     * @param pos `[x, y, z]` in physical space
     */
    convertCoordsFromPhysToCameraSpace(pos: vec3): vec3 | null {

        this.assertAlive();
        return this.map?.convertCoordsFromPhysToCameraSpace(pos) ?? null;
    }

    /**
     * Returns the geographic coordinates at the given canvas pixel, the
     * height measured from the datum.
     *
     * @param screenX canvas X coordinate in CSS pixels
     * @param screenY canvas Y coordinate in CSS pixels
     */
    getHitCoords(screenX: number, screenY: number): vec3 | null {

        this.assertAlive();
        return this.map?.getHitCoords(screenX, screenY) ?? null;
    }

    /**
     * Returns terrain distance at a 2D position in the current screen
     * view.
     *
     * @param screenX horizontal coordinate in the selected space
     * @param screenY vertical coordinate in the selected space
     * @param dilate depth-map dilation radius in hitmap pixels
     * @param useGeometricIntersection compute a geometric ray
     *   intersection instead of sampling the depth hitmap. Geocentric
     *   maps intersect the ellipsoid; projected maps intersect the
     *   base plane.
     * @param coordinateSpace coordinate space of `screenX` / `screenY`
     * @returns `[hit, distance]`, or null when the map is not ready.
     *   `distance` is the Euclidean distance from the viewer to the
     *   terrain surface at the selected position. When `hit` is false,
     *   no terrain covers the position and `distance` is a sentinel
     *   value.
     */
    getScreenDepth(
        screenX: number,
        screenY: number,
        dilate = 0,
        useGeometricIntersection = false,
        coordinateSpace: Renderer.CoordinateSpace = 'layout',
    ): [boolean, number] | null {

        this.assertAlive();
        return this.map?.getScreenDepth(
            screenX,
            screenY,
            dilate,
            useGeometricIntersection,
            coordinateSpace,
        ) ?? null;
    }

    // -----------------------------------------------------------------
    // Camera
    // -----------------------------------------------------------------

    /**
     * The camera the map renders from. Throws before a map is loaded.
     */
    get camera(): MapCamera {

        if (!this.camera_) throw new Error('No map is loaded.');
        return this.camera_;
    }

    /**
     * The camera terrain selection runs from: culling, texel-size
     * choice, depth sampling, and the vertical exaggeration scale. The
     * frozen clone while freeze mode is active, otherwise `camera`.
     */
    get selectionCamera(): MapCamera {

        return this.selectionCamera_ ?? this.camera;
    }

    /**
     * Freezes terrain selection at the given camera while `camera`
     * keeps moving; diagnostic freeze mode.
     */
    freezeSelection(camera: MapCamera): void {

        this.selectionCamera_ = camera;
        this.map?.markDirty();
    }

    /** Ends freeze mode: selection follows `camera` again. */
    unfreezeSelection(): void {

        this.selectionCamera_ = null;
        this.map?.markDirty();
    }

    /**
     * Sets the camera from a position. A refused position, such as a
     * `subj` one, leaves the camera unchanged.
     */
    setPosition(position: Map.PositionInput): void {

        const definition = positionCodecs.vts.decode(position);
        if (definition) this.camera.set(definition);
    }

    /** The camera as a position. */
    getPosition(): Map.PositionInput {

        return positionCodecs.vts.encode(this.camera.definition);
    }

    /**
     * Runs `callback` with the selection camera installed in the
     * renderer as the camera to draw from, and the view camera restored
     * afterwards. Calls `callback` with no swap while freeze mode is
     * inactive.
     *
     * @param callback  code to run under the selection camera
     * @returns the value returned by `callback`
     */
    withSelectionCamera<T>(callback: () => T): T {

        const selection = this.selectionCamera_;
        if (!selection) return callback();

        const renderer = this.renderer;

        renderer.installCamera(selection);

        try {

            return callback();

        } finally {

            renderer.installCamera(this.camera);
        }
    }

    // -----------------------------------------------------------------
    // Runtime sources and layers
    // -----------------------------------------------------------------

    /**
     * Creates a geodata builder for constructing vector free layers. Null
     * before a map is loaded. See `geodata-builder.d.ts` for its surface.
     */
    createGeodata(): MapGeodataBuilder | null {

        this.assertAlive();
        return this.map?.createGeodata() ?? null;
    }

    /**
     * Registers a data source of any type at runtime. Resolves once the
     * source is loaded and part of the usable map state; rejects if it
     * fails to load. Renders nothing on its own; a later `addLayer`
     * referencing it draws it.
     *
     * @param id source identifier
     * @param sourceSpec any cartolina source (surface, tms, or freelayer)
     */
    addSource(
        id: string,
        sourceSpec: StyleSchema.SourceSpecification,
    ): Promise<void> {

        this.assertAlive();
        return this.requireReadyStyle().addSource(id, sourceSpec);
    }

    /**
     * Removes a source registered through `addSource`.
     *
     * @param id source identifier
     */
    removeSource(id: string): void {

        this.assertAlive();
        this.requireReadyStyle().removeSource(id);
    }

    /**
     * Adds a style layer at runtime and re-commits the style.
     *
     * @param layerSpec a complete style layer carrying an explicit id
     */
    addLayer(layerSpec: StyleSchema.LayerSpecification): void {

        this.assertAlive();
        this.requireReadyStyle().addLayer(layerSpec);
    }

    /**
     * Removes a style layer added through `addLayer`.
     *
     * @param id layer identifier
     */
    removeLayer(id: string): void {

        this.assertAlive();
        this.requireReadyStyle().removeLayer(id);
    }

    // -----------------------------------------------------------------
    // Overlays
    // -----------------------------------------------------------------

    /**
     * Registers a custom overlay that runs as the explicit last step
     * of every canvas-target frame, after terrain, free layers, and
     * label / icon jobs have been drawn. Idempotent: a second call
     * with the same name is ignored.
     *
     * @param name unique overlay id
     * @param spec lifecycle callbacks; only `render` is required
     */
    addOverlay(name: string, spec: Map.OverlaySpec): void {

        this.assertAlive();
        if (this.findOverlayIndex(name) !== -1) return;

        this.overlays_.push({
            name, spec, enabled: true, added: false,
        });
    }

    /**
     * Removes the overlay registered under `name` and fires its
     * `onRemove` callback if `onAdd` had run.
     */
    removeOverlay(name: string): void {

        this.assertAlive();
        const index = this.findOverlayIndex(name);
        if (index === -1) return;

        const entry = this.overlays_[index];
        this.overlays_.splice(index, 1);

        if (entry.added && entry.spec.onRemove)
            entry.spec.onRemove(this.overlayContext());
    }

    /**
     * Toggles whether the overlay's `render` callback runs each frame.
     * Does not fire `onAdd` or `onRemove`.
     */
    setOverlayEnabled(name: string, enabled: boolean): void {

        this.assertAlive();
        const index = this.findOverlayIndex(name);
        if (index === -1) return;

        this.overlays_[index].enabled = enabled;
    }

    // -----------------------------------------------------------------
    // Frame loop
    // -----------------------------------------------------------------

    /**
     * Per-frame entry point. Called by `Core.onUpdate` once per
     * animation frame. Owns the public `tick` event, the `map-loaded`
     * first-load completion, position-change dispatch, canvas-target
     * sync, the dirty-gated draw, and the post-frame stats close.
     *
     * The two narrow callbacks `LegacyMap.tickBefore` and
     * `LegacyMap.tickDeferredEvents` carry residual JS work that has
     * not been promoted to TypeScript yet.
     */
    tick(): void {

        if (this.disposed_) return;

        // deliver pending config changes before any frame work
        this.configStore_.flush();

        const legacyMap = this.map;

        // No map loaded while the style is loading.
        if (legacyMap == null) {

            this.bus_.emit('tick', {});
            return;
        }

        // the vertical exaggeration the cameras derive their geometry
        // with: the configuration's generation and the selection view
        // extent
        const generation = this.verticalExaggeration.counter;
        const extent = this.selectionCamera.viewExtent;

        this.camera.setVerticalExaggeration(generation, extent);
        this.selectionCamera_?.setVerticalExaggeration(generation, extent);

        // First-load completion: fires once per map load.
        if (!this.mapLoadedFired_ && legacyMap.isReferenceFrameReady()) {

            legacyMap.srsReady = true;
            this.mapLoadedFired_ = true;

            // the store reads the parsed reference frame when it is
            // constructed, so it waits for this point
            this.elevationStore_ = new ElevationStore(this);

            // the view camera stays installed between passes; a pass
            // that draws from another camera swaps it in and back
            this.renderer.installCamera(this.camera);

            this.bus_.emit('map-loaded',
                { browserOptions: {} });
            this.markReady_();
        }

        // Reference frame still loading: only let the loader make
        // progress, emit `tick`, and bail out before opening a stats
        // frame. Mirrors the legacy `LegacyMap.update` not-ready
        // branch.
        if (!legacyMap.srsReady) {

            legacyMap.loader.update();
            this.bus_.emit('tick', {});
            return;
        }

        // take any completed async copy before this frame's depth queries
        if (this.renderer.hitmapMode > 2) this.renderer.collectHitmap();

        // Accept this tick's terrain height under the camera.
        const camera = this.camera;
        camera.update();

        // Position-change events.
        const lastDefinition = this.lastDefinition_;
        this.moving_ = !lastDefinition || !camera.equals(lastDefinition);
        if (this.moving_) {

            this.bus_.emit('map-position-changed', {
                'position': positionCodecs.vts.encode(camera.definition),
                'last-position': lastDefinition
                    ? positionCodecs.vts.encode(lastDefinition) : [],
            });
        }

        if (camera.terrainAltitude !== this.lastTerrainAltitude_) {

            this.bus_.emit('map-position-fixed-height-changed', {
                'height': camera.terrainAltitude,
                'last-height': this.lastTerrainAltitude_,
            });
        }

        this.lastDefinition_ = camera.definition;
        this.lastTerrainAltitude_ = camera.terrainAltitude;

        // Canvas size change re-sizes the caches and forces a redraw.
        if (this.renderer.ensureCanvasRenderTarget()) {

            legacyMap.setupCache();
            legacyMap.markDirty();
        }

        // the camera projects for the canvas
        const canvasSize = this.renderer.gpu.canvasRenderTarget.apparentSize;
        camera.setProjection({ aspect: canvasSize[0] / canvasSize[1] });

        const dirty = legacyMap.dirty || legacyMap.dirtyCountdown > 0;

        // fps clock starts
        legacyMap.stats.begin(dirty);
        legacyMap.tickBefore();

        // settle completed elevation lookups + update elevation store
        this.updateElevation();

        // prepare and/or draw if dirty
        if (dirty) {

            if (legacyMap.dirty) {
                legacyMap.dirtyCountdown =
                    legacyMap.config.mapRefreshCycles;
            } else {
                legacyMap.dirtyCountdown--;
            }

            legacyMap.dirty = false;
            legacyMap.bestMeshTexelSize = 0;
            legacyMap.bestGeodataTexelSize = 0;

            // Time only the rendering (draw + overlays); the loader and
            // event work below is not part of the frame's GPU cost.
            const profiler = this.frameProfiler();
            profiler.setGpuEnabled(legacyMap.config.mapProfileGpu === true);

            profiler.beginFrame();

            // draw
            this.draw();

            // overlays
            this.runOverlays();

            profiler.endFrame();
            this.publishFrameProfile();

            /* Post-draw loader promotion: requests discovered during
             * traversal enter the loader the same frame instead of
             * waiting one extra animation frame. */
            legacyMap.loader.update();

            this.bus_.emit('map-update', {});
        }

        legacyMap.tickDeferredEvents();
        // fps clock stops
        legacyMap.stats.end(dirty);
        this.bus_.emit('tick', {});
    }

    /**
     * Draws the colour frame against the canvas render target. Called
     * from `Map.tick`. The body relocates from the legacy
     * `MapDraw.drawMap`.
     *
     * This is the entry point that reaches the atmosphere, geodata,
     * labels, credits, and overlays.
     */
    draw(): void {

        const legacyMap = this.map!;
        const renderer = this.renderer;
        const mapDraw = legacyMap.draw;
        const gpu = renderer.gpu;

        // Reset owner-specific frame state before issuing draw work.
        legacyMap.visibleCredits = { imagery: {}, mapdata: {} };
        this.initFrame();
        renderer.initFrame();
        mapDraw.initFrame();

        gpu.clearColorAndDepth();

        // runtime atmosphere override falls back to the map
        // configuration.
        const atmosphereEnabled = this.overrides.flagAtmosphere
            ?? legacyMap.config.mapFlagAtmosphere;

        // iOS color-manages decoded PNG pixels, which corrupts the
        // density texture encoding (it rendered as concentric rings).
        // The gray-png decoder returns the stored bytes verbatim;
        // suppress the atmosphere only where that decoder cannot run
        // (iOS before 16.4).
        const atmosphereSupported = !utils.isIos()
            || grayPngDecodeAvailable();

        // draw background (skydome)
        if (atmosphereEnabled && atmosphereSupported
            && legacyMap.atmosphere)
            renderer.drawBackground();

        // runtime label override falls back to the map configuration.
        const labelsEnabled = this.overrides.flagLabels
            ?? legacyMap.config.mapFlagLabels;

        // clear queued geodata jobs
        if (labelsEnabled && legacyMap.freeLayerSequence.length > 0) {

            renderer.draw.clearJobBuffer();
        }

        // draw surfaces and free layers
        gpu.setState(mapDraw.drawTileState);
        if (this.overrides.drawEarth) {

            // A frame that only collects labels shows nothing, so it
            // draws no terrain. What a collected label reads of the
            // terrain comes from the elevation and depth passes.
            const collectsLabels = !!renderer.labelPlacement?.collected;

            // draw mesh tiles
            if (this.surfaceList().length > 0 && !collectsLabels)
                this.drawColorTerrain();

            // draw free layers
            const freeLayers = legacyMap.freeLayerSequence;
            for (let i = 0; i < freeLayers.length; i++) {

                const layer = freeLayers[i];

                if (!labelsEnabled) continue;

                if (layer.ready && layer.tree
                    && layer.stylesheet
                    && layer.stylesheet.isReady()) {

                    if (layer.type === 'vector') {
                        // monolithic geodata job collection
                        mapDraw.drawMonoliticGeodata(layer);
                    } else {
                        // Tiled geodata only collects jobs; labels and
                        // icons are drawn after terrain.
                        layer.tree.draw();
                    }
                }
            }

        } // if (this.overrides.drawEarth)

        // draw freeze frustum, if applicable
        const inspector = this.inspector;
        if (inspector && inspector.hasFreezeFrustum())
            inspector.drawFreezeFrustum();

        // draw queued geodata labels and icons
        if (this.overrides.drawEarth
                && labelsEnabled
                && legacyMap.freeLayerSequence.length > 0) {

            renderer.drawnGeodataTiles =
                legacyMap.stats.drawnGeodataTilesPerLayer;
            renderer.drawnGeodataTilesFactor =
                legacyMap.stats.drawnGeodataTilesFactor;

            renderer.draw.drawGpuJobs(this.selectionCamera.viewExtent);
        }

        // done
    }

    /**
     * Redraws the depth hitmap and starts an async copy to CPU, for
     * `getScreenDepth`/`getHitCoords` pixel reads. Throttled to one
     * redraw per `renderer.hitmapCopyIntervalMs`; while throttled, the
     * caller keeps reading the previous CPU copy and `hitMapDirty`
     * stays set so the redraw is retried next call.
     *
     * The hitmap serves the view camera's picture. A diagnostic may draw
     * it from the selection camera, under `withSelectionCamera`, to read
     * straight off the framebuffer; that draw is not throttled, starts no
     * CPU copy, and leaves the hitmap dirty so the next read redraws it
     * for the view.
     */
    updateDepthHitmap(): void {

        const legacyMap = this.map!;
        const renderer = this.renderer;
        const forView = renderer.mapCamera === this.camera;

        const interval = renderer.hitmapCopyIntervalMs;
        if (forView && interval > 0) {
            const now = Date.now();
            if ((renderer.lastHitmapCopyTime | 0) !== 0
                && (now - renderer.lastHitmapCopyTime) < interval) {
                return; // reuse previous CPU buffer this frame
            }
            renderer.lastHitmapCopyTime = now;
        }

        renderer.beginPass('depth');
        this.drawDepthHitmap();
        renderer.beginPass('base');

        // a copy of another camera's depth would be drained later as if
        // it were the view's, and the labels would be tested against it
        if (forView && renderer.hitmapMode > 2) renderer.copyHitmap();

        legacyMap.hitMapDirty = !forView;
        if (!forView) renderer.lastHitmapCopyTime = 0;
    }

    /**
     * Draws terrain into the depth hitmap target from the installed
     * camera. Called from `updateDepthHitmap`, which binds that target
     * first.
     *
     * The pass initializes the camera, renderer, and legacy draw state
     * its own traversal needs. Colour is cleared with the target by
     * `Renderer.beginPass`.
     */
    private drawDepthHitmap(): void {

        const legacyMap = this.map!;
        const renderer = this.renderer;

        this.initFrame();
        renderer.initFrame();
        legacyMap.draw.initFrame();

        renderer.gpu.clearDepth();
        renderer.gpu.setState(legacyMap.draw.drawTileState);

        renderer.recordHitmapCamera();

        if (!this.overrides.drawEarth) return;
        if (this.surfaceList().length === 0) return;

        this.drawTerrain({
            sink: new DepthTerrainSink(this),
            descent: this.viewDescent(),
        });
    }

    // -----------------------------------------------------------------
    // Canvas budgets
    // -----------------------------------------------------------------

    /**
     * Linear factor between the canvas's CSS resolution and the
     * resolution the map renders tiles at: `dpr ^ (mapPixelRatioUse /
     * 2)`, 1 on a ratio-1 display.
     */
    get pixelRatioScale(): number {

        const ratio =
            this.renderer.gpu.canvasRenderTarget.devicePixelRatio ?? 1;

        return Math.pow(ratio, this.config.mapPixelRatioUse / 2);
    }

    /**
     * Cache budgets in bytes for the current canvas: the configured
     * FullHD baselines scaled by the canvas area at the rendered
     * resolution, capped at `mapCacheScaleMax` times the baseline, and
     * never below a fixed floor per cache.
     */
    get cacheBudgets(): Map.CacheBudgets {

        const [width, height] =
            this.renderer.gpu.canvasRenderTarget.apparentSize;
        const linear = this.pixelRatioScale;
        const area = width * linear * height * linear;
        const scale = Math.min(
            area / FullHdPixels, this.config.mapCacheScaleMax);
        const budget = (baseline: number, floor: number): number =>
            Math.max(floor, baseline * scale) * 1024 * 1024;

        return {
            gpu: budget(this.config.mapGPUCache, 150),
            resource: budget(this.config.mapCache, 64),
            store: budget(this.config.mapElevationStoreGPUCache, 24),
        };
    }

    // -----------------------------------------------------------------
    // Terrain elevation
    // -----------------------------------------------------------------

    /** Whether the position changed at the start of the current tick. */
    get moving(): boolean {

        return this.moving_;
    }

    /**
     * Registers probes, positions whose terrain heights the map keeps
     * current. The set is updated from the elevation store every tick:
     * a height reads NaN until the terrain covering its probe is drawn,
     * then refines as finer terrain loads. Probes are copied and fixed
     * for the set's life; a moved probe requires a new registration.
     *
     * @param probes navigation-SRS positions, `[x, y]` each
     * @param desiredGsd ground sample distance asked for, in metres;
     *   zero asks for the finest terrain there is
     */
    registerSamples(
        probes: readonly ElevationStore.Position[],
        desiredGsd: number,
    ): Map.TerrainSamples {

        this.assertAlive();

        const samples = new ElevationStore.GeographicSampleSet(
            probes.map(([x, y]) => [x, y]), desiredGsd);

        this.registeredSamples_.push(samples);
        return samples;
    }

    /** Stops updating a registered set; its arrays keep their last
     * values. Ignores a set that is not registered. */
    releaseRegisteredSamples(samples: Map.TerrainSamples): void {

        const index = this.registeredSamples_.indexOf(samples);
        if (index === -1) return;

        this.registeredSamples_.splice(index, 1);
        this.elevationStore_?.disposeTerrainSamples(samples);
    }

    /**
     * Updates a retained terrain sample set in place.
     *
     * @internal The geodata heightcoding jobs drive their own sets;
     *   applications register theirs through `registerSamples`.
     */
    updateTerrainSamples(
        sampleSet: ElevationStore.SampleSet,
    ): Promise<boolean> {

        this.assertAlive();

        return this.elevationStore_
            ? this.elevationStore_.updateTerrainSamples(sampleSet)
            : Promise.resolve(false);
    }

    /**
     * Prevents a pending terrain-sample readback from writing to a set.
     *
     * @internal
     */
    disposeTerrainSamples(sampleSet: ElevationStore.SampleSet): void {

        this.elevationStore_?.disposeTerrainSamples(sampleSet);
    }

    /** Bumps when the elevation store discards its resident terrain. */
    get terrainEpoch(): number {

        return this.elevationStore_?.terrainEpoch ?? 0;
    }

    /**
     * Requested sample spacing for a prepared geodata view.
     * Tiled views use nominal node-SRS units for spatial-division sets;
     * monolithic views use ground metres for geographic sets.
     *
     * @param tileId geodata tile ID, or null for monolithic geodata
     * @param displaySize samples per tile side; used only for tiled views
     * @returns requested gsd in the sample set's units, or null before
     *   the reference frame or owning node is available, or when a
     *   monolithic view has no rendered terrain LOD
     * @internal Called by `MapGeodataView` before heightcoding updates.
     */
    geodataHeightcodingGsd(
        tileId: readonly number[] | null,
        displaySize: number,
    ): number | null {

        const legacyMap = this.map;
        const refFrame = legacyMap?.referenceFrame;
        if (!legacyMap || !refFrame) return null;

        if (tileId) {

            // Tiled geodata samples in its tile's spatial-division SRS.
            const node = refFrame.getSpatialDivisionNodeForTile(tileId);
            return node
                ? refFrame.getNodeGsd(node, tileId[0], displaySize)
                : null;
        }

        if (tileId === null) {

            // Monolithic geodata targets the highest rendered terrain LOD.
            let lod = -1;

            for (const key of Object.keys(legacyMap.stats.renderedLods))
                if (legacyMap.stats.renderedLods[Number(key)] > 0)
                    lod = Math.max(lod, Number(key));

            if (lod < 0) return null;

            // Approximate the whole set's ground spacing at the selection
            // center. Scale varies across the set, and the highest rendered
            // LOD can come from a different node subtree.
            const owner = refFrame.resolveSpatialDivisionNodes(
                this.selectionCamera.center).find((entry) => {

                    const partitioning = entry.node.partitioning;
                    return !(partitioning && typeof partitioning === 'object')
                        && partitioning !== 'none'
                        && partitioning !== 'barren';
                });

            if (!owner) return null;

            // Geographic sample sets request spacing in ground metres.
            return refFrame.getNodeGsd(owner.node, lod, 256)
                / owner.node.scaleFactor(owner.coords);
        }

        return null;
    }

    /**
     * GPU bytes the elevation store holds and may hold, zero before the
     * reference frame is ready.
     *
     * @internal Read by `MapStats` for the inspector panel.
     */
    elevationMemory(): { used: number; budget: number } {

        const store = this.elevationStore_;

        return {
            used: store ? store.usedBytes : 0,
            budget: store ? store.budgetBytes : 0,
        };
    }

    /**
     * Settles elevation lookups and runs an elevation pass when one is
     * due. Runs outside the dirty gate, so neither waits for a colour
     * frame.
     *
     * The pass descends to what the selection camera sees, as the
     * colour pass does, and under the registered positions down to the
     * LOD each needs.
     */
    private updateElevation(): void {

        const store = this.elevationStore_;
        if (!store) return;

        // Every registered set asks every tick; the store's per-set
        // interval makes a settled one cost one scan per interval.
        for (const samples of this.registeredSamples_)
            void store.updateTerrainSamples(samples);

        store.update();

        // Check for surfaces before admitting the pass, so a due pass
        // is not consumed and lost when there is nothing to draw.
        if (this.surfaceList().length === 0) return;
        if (!store.admitElevationPass()) return;

        const legacyMap = this.map!;

        const probeInterest = store.probeInterest(this.registeredSamples_);

        this.withSelectionCamera(() => {

            this.initFrame();
            this.renderer.initFrame();
            legacyMap.draw.initFrame();

            this.drawTerrain({
                sink: store.sink,
                descent: new ProbeDescent(
                    this.viewDescent(), probeInterest),
            });
        });
    }

    // -----------------------------------------------------------------
    // Surface trees
    // -----------------------------------------------------------------

    /**
     * Returns the surfaces the recursive draw traversal should render,
     * in back-to-front order (front surface at the last index). Plain
     * surfaces only — glues and virtual surfaces are skipped.
     *
     * The order comes from the current style's terrain sources array.
     */
    surfaceList(): TerrainSource[] {

        if (!this.style) return [];

        // terrain.sources order: back-to-front, front at last index
        const sources = this.style.style().terrain?.sources ?? [];

        return sources.map(
            sourceId => this.resolveTerrainSource(sourceId));
    }

    // -----------------------------------------------------------------
    // Migration shim
    // -----------------------------------------------------------------

    /**
     * The underlying legacy map, including the map currently being
     * populated by the style loader.
     *
     * @internal Internal browser infrastructure (inspector, control
     *   modes) still drives the full legacy map surface. These call
     *   sites are migration scaffolding rather than external
     *   consumers. Goes away with the legacy map.
     */
    get legacyMap(): LegacyMap | null {

        this.assertAlive();
        return this.map ?? this.loadingMap_;
    }

    // -----------------------------------------------------------------
    // Fields
    // -----------------------------------------------------------------

    /**
     * The loaded legacy map, or `null` before a map has loaded and
     * between loads.
     *
     * @internal The legacy field name is kept because the JS map,
     *   renderer, and inspector modules reach the loaded map as
     *   `core.map`, where `core` is this instance. Typed code should
     *   read `legacyMap`.
     */
    map: LegacyMap | null = null;

    /**
     * The WebGL2 renderer owned by this map.
     *
     * @internal Not part of the public API. Used by other `map/`
     *   internals (draw-traversal, style).
     */
    renderer!: Renderer;

    /**
     * Vertical exaggeration of the terrain, applied to model heights
     * before anything is drawn.
     *
     * @internal Not part of the public API. Used by other `map/`
     *   internals (style, atmosphere).
     */
    verticalExaggeration!: VerticalExaggeration;

    /**
     * The diagnostics inspector, or `null` when its module is
     * excluded from the build.
     *
     * @internal
     */
    inspector: Inspector | null;

    /**
     * Set by `GpuDevice` when the WebGL context is lost; stops the
     * frame loop.
     *
     * @internal
     */
    contextLost = false;

    /**
     * Legacy alias of the disposed state; checked by async resource
     * callbacks (`GpuTexture`) before touching the object.
     *
     * @internal
     */
    killed = false;

    /**
     * Request parameters for legacy binary loads.
     *
     * @internal
     */
    xhrParams: Record<string, unknown> = {};

    /**
     * The container element the canvas renders into; `null` after
     * disposal.
     *
     * @internal
     */
    element: HTMLElement | null;

    /**
     * The live, normalized config value map — the single config
     * object shared by the map, renderer, and browser layers.
     *
     * @internal
     */
    config: Readonly<viewerConfig.ViewerConfig>;

    /**
     * Application hook applied to every external resource request.
     *
     * @internal
     */
    readonly transformRequest:
        utils.TransformRequestCallback | undefined;

    /**
     * Runtime rendering overrides: diagnostic draw flags and per-frame
     * render-flag overrides. `Renderer.initFrame` caches a reference to
     * this object; all flag reads in a frame see the same snapshot.
     *
     * `draw*` fields default to `false`. `flag*` fields default to
     * `undefined`, which defers to the corresponding `ViewerConfig`
     * value. See `Overrides` in `src/map/overrides.ts` for the
     * full list.
     */
    overrides: Overrides = { ...defaultOverrides };

    /**
     * The loaded map's current style, or `null` before a map has
     * loaded and between loads. Installed by `MapStyle.loadStyle`.
     *
     * @internal Not part of the public API; read through
     *   `requireReadyStyle` for validated access.
     */
    style: MapStyle | null = null;

    // -----------------------------------------------------------------
    // LegacyMap factories
    // -----------------------------------------------------------------

    private async createMapFromStyle(
        style: unknown,
        path: string,
    ): Promise<void> {

        const legacyMap = new LegacyMap(
            this, path, this.config, this.bus_);
        legacyMap.outerMap = this;
        this.loadingMap_ = legacyMap;

        // the style loader's objects read the camera when constructed
        this.camera_ = new MapCamera(this, this.verticalExaggeration);

        try {

            // load style
            await MapStyle.loadStyle(
                this, style as StyleSchema.StyleSpecification);

            // no clue what these are
            const conv = new MapConvert(legacyMap);
            legacyMap.convert = conv;
            const meas = new MapMeasure(legacyMap);
            legacyMap.measure = meas;
            conv.measure = meas;

            legacyMap.isGeocent =
                !legacyMap.getNavigationSrs().isProjected();

            legacyMap.draw = new MapDraw(legacyMap);

            this.map = legacyMap;
            this.loadingMap_ = null;

        } catch (error) {

            legacyMap.kill();
            this.loadingMap_ = null;
            this.camera_ = null;
            this.style = null;
            this.rasterSources_.clear();
            this.terrainSources_.clear();
            throw error;
        }
    }

    // -----------------------------------------------------------------
    // Private helpers
    // -----------------------------------------------------------------

    /** Frame-loop entry: ticks, then schedules the next frame. */
    private onUpdate_(): void {

        if (this.killed || this.contextLost) return;

        this.tick();
        window.requestAnimationFrame(this.onUpdate_.bind(this));
    }

    /**
     * Resolves the `ready` Promise. Called once per map load by
     * `tick` when the reference frame first becomes ready.
     */
    private markReady_(): void {

        if (this.readyResolved_) return;
        this.readyResolved_ = true;
        this.resolveReady_?.();
        this.resolveReady_ = null;
        this.rejectReady_ = null;
    }

    /**
     * Reports a failure that left the map unloaded: logs it, rejects
     * `ready`, and fires the public `error` event.
     *
     * The error reaches the console only when no `error` listener is
     * registered, so an application that handles the event decides for
     * itself what the user sees.
     *
     * `ready` is rejected before the event so that a listener throwing
     * cannot leave it pending forever.
     *
     * @param cause the rejection value. Anything that is not an `Error`
     *   is wrapped in one, so `ready` and the event payload always
     *   carry the `Error` their types promise.
     */
    private reportLoadFailure(cause: unknown): void {

        const reason = cause instanceof Error
            ? cause
            : new Error(String(cause));

        this.rejectReady_?.(reason);

        if (!this.bus_.emit('error', { error: reason }))
            console.error(reason);
    }

    /** Kills and detaches the loaded legacy map, if any. */
    private destroyLoadedMap(): void {

        if (!this.map) return;

        // the store's units belong to this map's reference frame
        this.disposeElevationStore();
        this.registeredSamples_.length = 0;

        this.map.kill();
        this.map = null;
        this.camera_ = null;
        this.selectionCamera_ = null;
        this.lastDefinition_ = null;
        this.style = null;
        this.bus_.emit('map-unloaded', {});
    }

    /** Loads a map from a style URL or parsed style object. */
    private async loadMapFromStyle(
        style: string | StyleSchema.StyleSpecification,
        position?: Map.PositionInput,
    ): Promise<void> {

        let styleSpec: unknown = style;
        let path = window.location.href;

        if (typeof style === 'string') {

            path = utilsUrl.getProcessUrl(style, path);
            styleSpec = await utils.loadJson(
                path, this.transformRequest, 'Style');
        }

        await this.createMapFromStyle(styleSpec, path);

        // the style's position applies when the caller passed none
        const stylePosition =
            (styleSpec as StyleSchema.StyleSpecification).position;

        const initialPosition = position ?? stylePosition;

        if (initialPosition) this.setPosition(initialPosition);

        // initialize ubos
        this.renderer.createBuffers();
    }

    /**
     * Returns the loaded map's style state, throwing before
     * `viewer.ready` resolves: no validated layer ids, source ids, or
     * current terrain lists exist to change or query earlier.
     */
    private requireReadyStyle(): MapStyle {

        const style = this.readyResolved_ ? this.style : null;

        if (!style) {
            throw new Error('The style is not ready; await '
                + '`viewer.ready` before visibility changes or queries.');
        }

        return style;
    }

    /** The frame profiler, created on first use once the device exists. */
    private frameProfiler(): FrameProfiler {

        if (!this.frameProfiler_) {

            this.frameProfiler_ = new FrameProfiler(this.renderer.gpu);
        }

        return this.frameProfiler_;
    }

    /**
     * Publish the profiler result to the inspector stats and (when
     * `mapExposeFpsToWindow` is set) to `window`. Throttled because each
     * result sorts the sample windows; medians move slowly so a few
     * updates per second suffice.
     */
    private publishFrameProfile(): void {

        if ((this.profileWrite_++ % 15) !== 0) return;

        const legacyMap = this.map;
        if (!legacyMap) return;

        const profile = this.frameProfiler_!.result();
        legacyMap.stats.frameProfile = profile;

        if (legacyMap.config.mapExposeFpsToWindow
                && typeof window !== 'undefined') {

            const target = window as unknown as {
                __vtsFps?: number | null;
                __vtsPerf?: { frame?: FrameProfiler.Result };
            };

            target.__vtsFps = profile.limitFps;
            target.__vtsPerf = target.__vtsPerf ?? {};
            target.__vtsPerf.frame = profile;
        }
    }

    /** Throws if the map has been disposed. */
    private assertAlive(): void {

        if (this.disposed_) {
            throw new Error('Map has been destroyed.');
        }
    }

    /** Resets map-owned per-pass state. Called at the top of every pass. */
    private initFrame(): void {

        const legacyMap = this.map!;

        legacyMap.loader.setChannel(0); // 0 = hires channel
        legacyMap.stats.renderBuild = 0;
    }

    private findOverlayIndex(name: string): number {

        for (let i = 0; i < this.overlays_.length; i++) {

            if (this.overlays_[i].name === name) return i;
        }
        return -1;
    }

    private overlayContext(): Map.OverlayContext {

        return { renderer: this.renderer };
    }

    /**
     * Runs every registered overlay's `render` callback. Called as the
     * explicit last step of the canvas-target frame; must not be
     * called for any auxiliary pass.
     */
    private runOverlays(): void {

        if (this.overlays_.length === 0) return;

        const ctx = this.overlayContext();

        for (let i = 0; i < this.overlays_.length; i++) {

            const entry = this.overlays_[i];
            if (!entry.enabled) continue;

            if (!entry.added) {

                if (entry.spec.onAdd) entry.spec.onAdd(ctx);
                entry.added = true;
            }

            entry.spec.render(ctx);
        }
    }

    /**
     * Fires `onRemove` for every added overlay in registration-reverse
     * order. Called from `[Symbol.dispose]` before tearing down the
     * renderer so overlays can still draw through it.
     */
    private disposeOverlays(): void {

        const ctx = this.overlayContext();
        for (let i = this.overlays_.length - 1; i >= 0; i--) {

            const entry = this.overlays_[i];
            if (entry.added && entry.spec.onRemove)
                entry.spec.onRemove(ctx);
        }
        this.overlays_.length = 0;
    }

    /**
     * Releases the elevation store and settles every lookup it holds.
     * Safe to call when no store exists.
     */
    private disposeElevationStore(): void {

        if (!this.elevationStore_) return;

        this.elevationStore_[Symbol.dispose]();
        this.elevationStore_ = null;
    }

    /**
     * Releases the recursive terrain traversal mask pool. Safe to call
     * when no pool has been allocated. Invoked on map unload and from
     * `[Symbol.dispose]()`; the renderer's GL context stays alive
     * across unloads, so the pool's textures are valid to release.
     */
    private disposeTerrainMaskPool(): void {

        if (!this.terrainMaskPool_) return;

        this.terrainMaskPool_.dispose();
        this.terrainMaskPool_ = null;
    }

    /**
     * Runs the colour frame's terrain pass, with the accounting that
     * belongs to the visible frame.
     */
    private drawColorTerrain(): void {

        const legacyMap = this.map!;

        const accounting: TerrainTraversalAccounting = {
            drawCounter: legacyMap.draw.drawCounter,
            usedNodes: 0,
            processedNodes: 0,
            processedMetatiles: 0,
        };

        this.drawTerrain({
            sink: new ColorTerrainSink(this),
            descent: this.viewDescent(),
            accounting,
        });

        const stats = legacyMap.stats;
        stats.usedNodes = accounting.usedNodes;
        stats.processedNodes = accounting.processedNodes;
        stats.processedMetatiles = accounting.processedMetatiles;
    }

    /** The descent of a pass that follows the selection camera. */
    private viewDescent(): ViewDescent {

        return new ViewDescent(this.selectionCamera);
    }

    /**
     * Recursive terrain traversal — the only surface traversal path.
     * Feeds the combined-descent traversal in `draw-traversal.ts` with
     * the current `surfaceList()`, the cached per-surface helper trees,
     * and the calling pass's sink and state. Glues and virtual surfaces
     * are excluded — see `rfc03-draw-traversal.md` §7.
     */
    private drawTerrain(pass: TerrainTraversalPass): void {

        const resolution = this.config.mapTraversalMaskResolution;

        if (this.terrainMaskPool_
                && this.terrainMaskPool_.resolution !== resolution) {

            // Config changed at runtime; rebuild for the new size.
            this.terrainMaskPool_.dispose();
            this.terrainMaskPool_ = null;
        }

        if (!this.terrainMaskPool_) {
            this.terrainMaskPool_ = new DrawTraversalMaskPool(
                this.renderer, resolution);
        }

        const trees = this.resolveSurfaceTrees();
        if (trees.length === 0) return;

        // Terrain resources enter the GPU cache all through the pass.
        // A tile draws right after the traversal finds it ready, so an
        // eviction while the pass runs can take the mesh of a tile the
        // traversal is about to draw. The cache evicts nothing until the
        // cost check below, which keeps eviction out of that window.
        const gpuCache = this.map!.gpuCache;

        gpuCache.skipCostCheck = true;

        try {
            drawTerrainTraversal(this, trees, this.terrainMaskPool_!, pass);
        } finally {

            gpuCache.skipCostCheck = false;
            gpuCache.checkCost();
        }
    }

    /**
     * Resolves `surfaceList()` to per-surface helper trees, allocating
     * new ones on demand and dropping cache entries whose surface has
     * left the view. Returns the trees in `surfaceList()` order.
     */
    private resolveSurfaceTrees(): MapSurfaceTree[] {

        const legacyMap = this.map;
        if (!legacyMap) return [];

        const cache = this.surfaceTrees_;
        const surfaces = this.surfaceList();
        const result: MapSurfaceTree[] = [];
        const live = new Set<string>();

        for (const surface of surfaces) {

            const id = surface.id;
            live.add(id);

            let surfaceTree = cache.get(id);
            if (!surfaceTree) {

                // Construct a single-surface tree by passing the
                // surface as `freeLayerSurface`; every tile then binds
                // directly to that surface, giving the tree direct,
                // per-surface metatile lookups.
                surfaceTree = new MapSurfaceTree(legacyMap, true, surface);
                cache.set(id, surfaceTree);
            }

            result.push(surfaceTree);
        }

        // Drop helper trees for surfaces that are no longer in view.
        for (const id of cache.keys()) {

            if (!live.has(id)) {

                cache.get(id)!.surfaceTree?.kill();
                cache.delete(id);
            }
        }

        return result;
    }

    // -----------------------------------------------------------------
    // Private fields
    // -----------------------------------------------------------------

    private disposed_ = false;

    private moving_ = false;

    /** The camera, or null before a map is loaded and between loads. */
    private camera_: MapCamera | null = null;

    /** The frozen selection camera while freeze mode is active. */
    private selectionCamera_: MapCamera | null = null;

    /** The camera definition and terrain height the last tick's
     * position events were computed against. */
    private lastDefinition_: MapCamera.Definition | null = null;
    private lastTerrainAltitude_ = 0;

    /**
     * The elevation store, or null before the reference frame is ready
     * and between map loads.
     */
    private elevationStore_: ElevationStore | null = null;

    /** The terrain sample sets registered through `registerSamples`,
     * updated every tick. */
    private registeredSamples_: ElevationStore.GeographicSampleSet[] = [];

    /**
     * Legacy map currently being populated by the style loader.
     *
     * Kept separate from `map` so the frame loop cannot observe partial
     * construction. Typed source constructors reach it through
     * `legacyMap` until credit ownership moves into this class.
     */
    private loadingMap_: LegacyMap | null = null;

    private readyPromise_: Promise<void>;

    private readyResolved_ = false;

    private resolveReady_: (() => void) | null = null;

    private rejectReady_: ((reason: Error) => void) | null = null;

    /**
     * Resolved raster sources and permanent load failures, keyed by style
     * source id. Raster source instances are owned here rather than by the
     * legacy JavaScript half of the map.
     */
    private rasterSources_ =
        new globalThis.Map<string, Map.RasterSourceEntry>();

    /**
     * Resolved terrain sources, keyed by style source id. A surface
     * definition that fails to load rejects style loading, so a
     * registered entry is always usable.
     */
    private terrainSources_ = new globalThis.Map<string, TerrainSource>();

    /**
     * The event bus behind `on`, `once`, and `emit`. Handed to the
     * legacy emitters (`LegacyMap`, `GpuDevice`) at their
     * construction; they publish through it directly.
     */
    private bus_: EventBus<Map.ViewerEventMap> = new EventBus();

    /**
     * The runtime config store. Owned by `Viewer`, which seeds it
     * with the caller's options before constructing this `Map`;
     * `Map.tick` flushes it at the start of every frame so watchers
     * reconfigure before the draw.
     */
    private configStore_: ConfigStore<viewerConfig.ViewerConfig>;

    /**
     * Registered overlays in registration order. `onAdd` fires the
     * first time an entry runs through `runOverlays_`; `onRemove`
     * fires when the entry is removed or when the `Map` is disposed.
     */
    private overlays_: OverlayEntry[] = [];

    /** Did the one-time `map-loaded` completion fire for the loaded
     * map already? Reset implicitly by replacing the typed `Map`. */
    private mapLoadedFired_ = false;

    /**
     * UV-space mask pool used by the recursive terrain traversal.
     * Lazily allocated on first use by `drawTerrainRecursive` and
     * disposed on map unload and `[Symbol.dispose]()`. Removal
     * target in phase 8 alongside the legacy traversal.
     */
    private terrainMaskPool_: DrawTraversalMaskPool | null = null;

    /**
     * Per-drawn-frame timing (cpu, gpu, draw-call and FBO-switch counts),
     * feeding the inspector panel and `window.__vtsPerf`. Lazily created
     * on the first drawn frame once the GPU device exists.
     */
    private frameProfiler_: FrameProfiler | null = null;

    /** Throttle for publishing profiler results (every Nth drawn frame). */
    private profileWrite_ = 0;

    /**
     * Per-surface helper trees used by the recursive draw traversal
     * (rfc03-draw-traversal.md §2.1). Keyed by surface id. Each tree is
     * a single-surface `MapSurfaceTree` constructed with the surface
     * as its `freeLayerSurface`, so every tile binds directly to that
     * surface. The cache is refreshed against
     * the current `surfaceList()` on every draw; entries for surfaces
     * that have left the view are dropped.
     */
    private surfaceTrees_: globalThis.Map<string, MapSurfaceTree>
        = new globalThis.Map();
}


// Local non-exported types used by the class above.

type OverlayEntry = {
    name: string;
    spec: Map.OverlaySpec;
    enabled: boolean;
    added: boolean;
};


/* Public types exposed under `Map.*`. Consumers (`Viewer`, demos,
 * sibling modules) reference them as `Map.OverlaySpec`,
 * `Map.ViewerEventMap`, etc. Types owned by this module are authored
 * here; types owned elsewhere forward to their canonical module. The
 * same-name namespace pattern is documented in AGENTS.md. */
namespace Map {

    /** Cache budgets in bytes for the current canvas. */
    export type CacheBudgets = {
        gpu: number;
        resource: number;
        store: number;
    };

    export type RasterSourceEntry =
        | { status: 'ready'; source: RasterSource }
        | { status: 'failed'; error: Error };

    /**
     * A complete visibility snapshot: the active terrain stack plus
     * the active terrain-source list of every style layer.
     */
    export type VisibilityProfile = {
        terrain: string[];
        layers: Record<string, string[]>;
    };

    /**
     * Per-frame context passed to overlay lifecycle callbacks.
     *
     * Overlays run as the explicit last step of the canvas-target
     * frame, after the engine has finished drawing terrain, free
     * layers, and label/icon jobs. Forward-compatible: new context
     * fields may be added; existing callbacks need no change.
     */
    export type OverlayContext = {
        /**
         * The renderer for issuing draw helpers (`drawImage`,
         * `drawLineString`, `createTexture`, `getCanvasSize`).
         */
        readonly renderer: Renderer;
    };

    /**
     * Lifecycle hooks for a custom overlay registered through
     * `viewer.addOverlay(name, spec)`.
     *
     * - `onAdd` fires once when the engine is ready to accept draw
     *   calls from the overlay (after `map-loaded`). Overlays
     *   registered before the engine is ready have `onAdd` deferred
     *   to that moment.
     * - `render` fires every frame, as the last step against the
     *   canvas target, after engine draws have completed.
     * - `onRemove` fires when the overlay is removed via
     *   `viewer.removeOverlay(name)` or when the viewer is disposed.
     */
    export type OverlaySpec = {
        onAdd?: (ctx: OverlayContext) => void;
        render: (ctx: OverlayContext) => void;
        onRemove?: (ctx: OverlayContext) => void;
    };

    /**
     * Map from event name to its payload type. All events are public
     * and reachable through `Viewer.on()`.
     *
     * Known payload fields carry concrete types; `unknown` remains
     * only where the source is still untyped ES5 and the shape cannot
     * be verified without migrating that file.
     */
    export type ViewerEventMap = {

        /**
         * A failure that left the map unloaded, most often an invalid
         * style or an unreachable style URL. The same error rejects
         * `Viewer.ready` and is written to the console only when no
         * `error` listener receives it.
         */
        'error': { error: Error };

        'map-loaded': { browserOptions: Record<string, unknown> };
        'map-unloaded': Record<string, never>;
        'map-update': Record<string, never>;
        'map-position-changed': {
            position: PositionInput;
            'last-position': PositionInput;
        };
        'map-position-fixed-height-changed': {
            height: number;
            'last-height': number;
        };
        'map-position-panned': Record<string, never>;
        'map-position-rotated': Record<string, never>;
        'map-position-zoomed': Record<string, never>;
        'tick': Record<string, never>;
        'gpu-context-lost': Record<string, never>;
        'gpu-context-restored': Record<string, never>;
        'geo-feature-enter': GeoFeatureEvent;
        'geo-feature-leave': GeoFeatureEvent;
        'geo-feature-hover': GeoFeatureEvent;
        'geo-feature-click': GeoFeatureEvent;
        'autorotate-changed': { autorotate: number };
        'fly-start': {
            startPosition: CameraDefinition;
            endPosition: CameraDefinition;
            options: unknown;
        };
        'fly-final-phase': { position: CameraDefinition };
        'fly-progress': { position: CameraDefinition; progress: number };
        'fly-end': { position: CameraDefinition };
        'loading-screen-hidden': Record<string, never>;
    };

    /**
     * Payload of the `geo-feature-*` pointer events emitted by
     * `LegacyMap` (`src/map/legacy-map.js`).
     */
    export type GeoFeatureEvent = {
        feature: unknown; // typed once LegacyMap migrates to TS
        'canvas-coords': number[];
        'physical-coords': number[];
        state: unknown;
        element: unknown;
    };

    // Types owned by other modules, forwarded to their canonical
    // source so this namespace is the single public entry point.

    /**
     * A camera position in the vts-geospatial ten-component form: view
     * mode, x, y, height mode, height, yaw, pitch, roll, view extent,
     * field of view.
     */
    export type PositionInput = positionCodecs.vts.Position;

    export type CameraDefinition = MapCamera.Definition;
    export type AltitudeRef = MapCamera.AltitudeRef;

    /**
     * A terrain sample set registered through `registerSamples`:
     * its positions and requested gsd, and the store-written heights,
     * gsds, `settled` flag and `generation` counter. Positions and
     * heights are navigation space: on a geocentric frame, longitude and
     * latitude on the reference ellipsoid and geodetic height above it.
     */
    export type TerrainSamples = ElevationStore.GeographicSampleSet;

    /** Shaping options of a flight generated by `flyTo`. */
    export type FlyToOptions = {

        /** `direct` interpolates, `ballistic` arcs over the distance,
         * `piha` (perceived-velocity invariant horizontal autopilot)
         * flies at a steady perceived speed over the ground with the
         * altitude following the velocity; `auto` picks `direct` or
         * `ballistic` by distance. */
        mode?: 'auto' | 'direct' | 'ballistic' | 'piha';

        /** Perceived ground speed of a `piha` flight, as the fraction
         * of the view that passes per second. */
        pv?: number;

        /** Ceiling of a ballistic arc, in metres. */
        maxHeight?: number;
        minDuration?: number;
        maxDuration?: number;
        samplePeriod?: number;
        fade?: 'none' | 'in' | 'out' | 'inout';
        fadePower?: number;
        yawInterpolation?: 'shortest' | 'longest' | 'none';

        /** Height of a ballistic arc above the higher end. */
        height?: number;
        speed?: number;
    };

    export type ViewerConfig = import('../viewer-config').ViewerConfig;
    export type Lod = import('./types').Lod;
    export type VerticalExaggerationSpec = VerticalExaggeration.Spec;
}


export default Map;
